import { ContentType, UrlParser, SnippetOnlyDomains } from "@authory-packages/core";
import { ItemPreviewEdit, ItemPreviewFormik } from "../../ItemPreviewEdit";
import { truncate } from "../../../utils/article-text-utils";
import { MAX_DESCRIPTION_SIZE } from "../../../types/max_description_size";
import { V3BodyGrey35 } from "../../ReusableStyledComponents";
import { getContentProcessLabel, isSnippetImportFailed } from "./AddContent.utils";
import { prepareURL } from "../../../utils/prepareURL";
import { FinalImportResult, ImportItem } from "./AddContent.types";
import { pickSuggestedSource } from "../../../shared-logic/pickSuggestedSource";
import { NoPreviewFoundPrompt } from "./NoPreviewFoundPrompt";

interface AddContentProcessProps {
    sourcesOptions: { value: string; text: string | null; url: string }[];
    onNextText: string;
    currProcessingItem: ImportItem;
    isBulkImport?: boolean;
    isPlugin?: boolean;
    targetContentType: ContentType;
    onImportSubmit: (data: FinalImportResult) => Promise<void>;
    onTemporaryImageUploadHandler: (file: File) => Promise<string>;
    onBackHandler?: () => void;
    onSkip?: () => void;
}

export const AddContentProcess = ({
    currProcessingItem,
    sourcesOptions,
    onNextText,
    isBulkImport,
    isPlugin = false,
    targetContentType,
    onTemporaryImageUploadHandler,
    onImportSubmit,
    onBackHandler,
    onSkip
}: AddContentProcessProps) => {
    // Trim descriptions
    if (!!currProcessingItem?.contentRes?.description)
        currProcessingItem.contentRes.description = truncate(currProcessingItem.contentRes.description, MAX_DESCRIPTION_SIZE, true);
    if (!!currProcessingItem?.snippetRes?.description)
        currProcessingItem.snippetRes.description = truncate(currProcessingItem.snippetRes.description, MAX_DESCRIPTION_SIZE, true);

    const importResponse = currProcessingItem?.contentRes;
    const snippetResponse = currProcessingItem?.snippetRes;

    let isSnippetOnlydomain = false;

    // snippet only urls, dont set as active by default
    try {
        if (currProcessingItem.url) {
            const domain = UrlParser.extractDomain(currProcessingItem.url);

            if (domain && domain in SnippetOnlyDomains) {
                isSnippetOnlydomain = true;
            }
        }
    } catch {}

    const { existingSourceSuggestion, allowCreateNewSource, newSource, newSourceNameSuggestion } =
        pickSuggestedSource(targetContentType, currProcessingItem.url, sourcesOptions) || "";

    // Content type chain of priorities
    const finalType = importResponse?.type || targetContentType;

    // Both metadata calls came back empty, so there is nothing to prefill - the extension is the better route.
    // Irrelevant in the plugin itself, and for file uploads, which always carry their own import response.
    const noPreviewFound = !importResponse && isSnippetImportFailed(snippetResponse) && !isPlugin && !!currProcessingItem.url;

    const onItemPreviewSubmit = async (data: ItemPreviewFormik) => {
        const url = importResponse?.url || currProcessingItem.url || undefined;
        const prepareUrl = url ? prepareURL(url) : undefined;
        const canonicalUrl = snippetResponse?.canonicalUrl ? prepareURL(snippetResponse.canonicalUrl) : undefined;

        const payload: FinalImportResult = {
            ...data,
            importResult: importResponse,
            type: finalType,
            url: prepareUrl,
            canonicalUrl,
            isSnippetOnlydomain
        };

        await onImportSubmit(payload);
    };

    return (
        <ItemPreviewEdit
            mainParagraph={
                noPreviewFound ? (
                    <NoPreviewFoundPrompt />
                ) : (
                    <V3BodyGrey35>
                        {getContentProcessLabel(finalType, sourcesOptions.length > 0, !!importResponse, importResponse?.content.type)}
                    </V3BodyGrey35>
                )
            }
            title={importResponse?.title || snippetResponse?.title || ""}
            description={importResponse?.description || snippetResponse?.description || ""}
            date={importResponse?.date || snippetResponse?.date || ""}
            sourceSlug={existingSourceSuggestion}
            newSource={newSource}
            isPlugin={isPlugin}
            onNextText={onNextText}
            sourcesOptions={sourcesOptions}
            allowCreateNewSource={allowCreateNewSource}
            isAddContentFlow
            name={newSourceNameSuggestion}
            pageUrl={currProcessingItem.url || ""}
            previewImage={importResponse?.previewImage || snippetResponse?.thumbnailImage}
            contentType={finalType}
            contentDetailType={importResponse?.content?.type}
            onTemporaryImageUploadHandler={onTemporaryImageUploadHandler}
            style={null}
            onItemPreviewSubmit={onItemPreviewSubmit}
            isBulkImport={isBulkImport}
            onBackHandler={onBackHandler}
            onSkip={onSkip}
        />
    );
};
